import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  buildPlmItemId,
  type PlmItem,
  type PlmType,
} from '@/utils/approval/productLifecycle';

/** 조회·작성 화면이 이 클래스로 포탈 다운로드 버튼을 그림 */
export const DOWNLOAD_BUTTON_CLASS = 'plm-download';

export const CARD_CLASS = 'plm-card';

const text = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim();

/** 레거시 number_format */
const formatNumber = (v: unknown) => {
  const raw = text(v);
  const n = Number(raw.replace(/,/g, ''));
  return raw && Number.isFinite(n) ? n.toLocaleString('en-US') : raw;
};

/** 레거시 appr_sub_view_forms_pdm_mt_mstr — 이미지 + 스타일 정보 표 */
const PlmCardView = ({
  item,
  plmType,
  t,
}: {
  item: PlmItem;
  plmType: PlmType;
  t: TFunction;
}) => {
  const { card } = item;
  const imageUrl = text(card.imageUrl);
  return (
    <div
      className={CARD_CLASS}
      data-key={item.key}
      data-item-id={buildPlmItemId(plmType, {
        aprvId: card.aprvId,
        brand: card.brand,
        yy: card.yy || card.year,
        styleNo: card.styleNo,
      })}
      data-style-no={text(card.styleNo)}
    >
      <table className="table table-bordered">
        <colgroup>
          <col width="16%" />
          <col width="12%" />
          <col width="20%" />
          <col width="13%" />
          <col width="14%" />
          <col width="12%" />
          <col width="13%" />
        </colgroup>
        <tbody>
          <tr>
            <td rowSpan={6} className="text-center plm-image">
              {imageUrl && <img src={imageUrl} alt="" />}
            </td>
            <td colSpan={4} className="text-center bg-info">
              {`${text(card.styleNo)}-${text(card.styleName)}`}
            </td>
            <td colSpan={2} className="text-right bg-info">
              <button
                type="button"
                className={DOWNLOAD_BUTTON_CLASS}
                data-pdfurl={text(card.pdfUrl)}
                data-load={plmType}
                data-item-key={item.key}
              >
                {t('상세내역 다운로드')}
              </button>
            </td>
          </tr>
          <tr className="text-center">
            <td rowSpan={4} className="label">
              {t('브랜드')}
            </td>
            <td rowSpan={4}>{text(card.brandName)}</td>
            <td className="label">{t('품번')}</td>
            <td colSpan={3}>{text(card.styleNo)}</td>
          </tr>
          <tr className="text-center">
            <td className="label">{t('운영시즌')}</td>
            <td colSpan={3}>{text(card.seasonRun)}</td>
          </tr>
          <tr className="text-center">
            <td className="label">{t('패밀리품번')}</td>
            <td colSpan={3}>{text(card.familyStyleNo)}</td>
          </tr>
          <tr className="text-center">
            <td className="label">{t('성별')}</td>
            <td>{text(card.sex)}</td>
            <td rowSpan={2} className="label">
              {t('가판가')}
            </td>
            <td rowSpan={2}>{formatNumber(card.tempCost)}</td>
          </tr>
          <tr className="text-center">
            <td className="label">{t('대구분')}</td>
            <td>{text(card.typeName)}</td>
            <td className="label">{t('복종그룹')}</td>
            <td>{text(card.clothesGroup)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export const buildPlmCardHtml = (
  item: PlmItem,
  plmType: PlmType,
  t: TFunction,
): string =>
  renderToStaticMarkup(<PlmCardView item={item} plmType={plmType} t={t} />);

/** 저장 본문 — 고른 순서대로 카드 */
export const buildPlmContentHtml = (
  items: PlmItem[],
  plmType: PlmType,
  t: TFunction,
): string => items.map((item) => buildPlmCardHtml(item, plmType, t)).join('');
